<template>
  <v-table density="compact" data-testid="vehicle-draft-manual-overrides-table">
    <thead>
      <tr>
        <th>Field</th>
        <th>Action</th>
        <th>Current value</th>
        <th>New value</th>
        <th>Release policy</th>
        <th v-if="showRevert" class="text-right">Action</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="row in rows" :key="`${vin}-${row.fieldKey}`">
        <td>
          <strong>{{ formatFieldLabel(row.fieldKey) }}</strong>
          <div v-if="row.changedBy || row.updatedAt" class="text-caption text-grey">
            <span v-if="row.changedBy">By {{ row.changedBy }}</span>
            <span v-if="row.updatedAt" class="ml-2">
              <DateTimeWithDealerTooltip :value="row.updatedAt" />
            </span>
          </div>
        </td>
        <td>{{ formatManualOverrideActionLabel(row.action) }}</td>
        <template v-if="isPhotoOverrideRow(row)">
          <td colspan="2" data-testid="vehicle-draft-dealer-photos-cell">
            <template v-if="photoDiffFor(row)">
              <DealerPhotoDiffThumbnails
                v-if="hasDealerPhotoVisualChanges(photoDiffFor(row)!)"
                :diff="photoDiffFor(row)!"
              />
              <span v-else class="text-body-2 text-medium-emphasis" data-testid="vehicle-draft-dealer-photos-unchanged">
                No photo changes compared with the live photos.
              </span>
            </template>
            <div v-else class="d-flex flex-column ga-2 py-2" data-testid="vehicle-draft-dealer-photos-new">
              <div class="text-body-2 text-medium-emphasis" data-testid="vehicle-draft-dealer-photos-replaces-note">
                These photos replace the vehicle's current photos when you publish.
              </div>
              <div class="d-flex flex-wrap ga-2">
                <a
                  v-for="(url, index) in photoUrlsFor(row)"
                  :key="`new-photo-${index}-${url}`"
                  :href="url"
                  target="_blank"
                  rel="noopener"
                  class="text-decoration-none"
                >
                  <v-img
                    :src="url"
                    :alt="`New photo ${index + 1}`"
                    width="72"
                    height="54"
                    cover
                    class="rounded border"
                  />
                </a>
              </div>
            </div>
          </td>
        </template>
        <template v-else>
          <td>
            <template v-if="row.action === 'REMOVE_OVERRIDE'">
              <span class="text-body-2 text-medium-emphasis">
                {{ formatManualOverrideRemoveMessage(row.fieldKey) }}
              </span>
            </template>
            <code v-else>{{ formatBaseValue(row.fieldKey, row.mappedValueAtLock) }}</code>
          </td>
          <td>
            <code v-if="row.action !== 'REMOVE_OVERRIDE'">
              {{ formatFieldValue(row.fieldKey, row.manualValue) }}
            </code>
            <span v-else class="text-grey">—</span>
          </td>
        </template>
        <td>
          <span v-if="row.action !== 'REMOVE_OVERRIDE'">
            {{ formatManualOverrideReleasePolicyLabel(row.releasePolicy, row.expiresAt, dealerTimeZone) }}
          </span>
          <span v-else class="text-grey">—</span>
        </td>
        <td v-if="showRevert" class="text-right">
          <v-btn
            size="small"
            variant="text"
            color="primary"
            :loading="isRevertingField(row.fieldKey)"
            :disabled="revertDisabled"
            @click="$emit('revert', row.fieldKey)"
          >
            Revert
          </v-btn>
        </td>
      </tr>
    </tbody>
  </v-table>
</template>

<script setup lang="ts">
import DateTimeWithDealerTooltip from '@/components/DateTimeWithDealerTooltip.vue'
import DealerPhotoDiffThumbnails from '@/components/DealerPhotoDiffThumbnails.vue'
import {
  buildDealerPhotoDiff,
  hasDealerPhotoVisualChanges,
  type DealerPhotoDiff,
} from '@/utils/dealerPhotoDiff'
import {
  formatManualOverrideActionLabel,
  formatManualOverrideRemoveMessage,
  formatManualOverrideDraftFieldValue,
  formatManualOverrideReleasePolicyLabel,
  formatVehicleDraftFieldLabel,
  type ManualOverrideDraftDisplayRow,
} from '@/utils/manualOverrideDraftDisplay'

const props = withDefaults(
  defineProps<{
    vin: string
    rows: ManualOverrideDraftDisplayRow[]
    showRevert?: boolean
    revertDisabled?: boolean
    isReverting?: Record<string, boolean>
    dealerTimeZone?: string
    /**
     * dealer_photos override rows (ADD/UPDATE):
     * - 'diff': before = `livePhotos`, after = manualValue (falls back to 'new-only' when the live photos
     *   are not available);
     * - 'new-only': show the new photos with a "replaces current photos" note (no before/after diff).
     */
    photoMode?: 'diff' | 'new-only'
    /** The vehicle's currently live photos (the "before" side in 'diff' mode). */
    livePhotos?: string[] | null
  }>(),
  {
    showRevert: false,
    revertDisabled: false,
    isReverting: () => ({}),
    dealerTimeZone: '',
    photoMode: 'diff',
    livePhotos: null,
  },
)

defineEmits<{
  revert: [fieldKey: string]
}>()

const formatFieldLabel = (fieldKey: string): string => formatVehicleDraftFieldLabel(fieldKey)

const formatFieldValue = (fieldKey: string, value: unknown): string =>
  formatManualOverrideDraftFieldValue(fieldKey, value, {
    dealerTimeZone: props.dealerTimeZone,
  })

const formatBaseValue = (fieldKey: string, value: unknown): string => {
  if (value === null || value === undefined) return 'No feed value'
  return formatFieldValue(fieldKey, value)
}

const isPhotoOverrideRow = (row: ManualOverrideDraftDisplayRow): boolean =>
  row.fieldKey === 'dealer_photos' && row.action !== 'REMOVE_OVERRIDE'

const photoUrlsFor = (row: ManualOverrideDraftDisplayRow): string[] =>
  Array.isArray(row.manualValue)
    ? row.manualValue.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    : []

const photoDiffFor = (row: ManualOverrideDraftDisplayRow): DealerPhotoDiff | null => {
  if (props.photoMode !== 'diff' || !Array.isArray(props.livePhotos)) return null
  return buildDealerPhotoDiff(props.livePhotos, photoUrlsFor(row))
}

const isRevertingField = (fieldKey: string): boolean => {
  const vin = String(props.vin || '')
    .trim()
    .toUpperCase()
  return Boolean(props.isReverting?.[`vehicleDraftManualOverride:${vin}:${fieldKey}`])
}
</script>
